import { useEffect, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  acceptInvite as requestInviteAcceptance,
  acceptOrgInvite as requestOrgInviteAcceptance,
  completePasswordChange as requestPasswordChangeCompletion,
  completePayment as requestPaymentCompletion,
  completeTermsAcceptance as requestTermsAcceptanceCompletion,
  openNotificationEvent as requestNotificationEvent,
  resetPassword as requestPasswordReset,
  restoreSession,
  signIn,
  signOut,
  startSupportSession as requestSupportSessionStart,
  stopSupportSession as requestSupportSessionStop,
  switchInternshipContext as requestInternshipContextSwitch,
  switchRoleContext as requestRoleContextSwitch,
  verifyLoginCode as requestLoginCodeVerification,
} from '@/entities/session/api/sessionClient'
import type { Session } from '@/entities/session/model/session'
import { SessionContext, type SessionContextValue } from '@/entities/session/model/SessionContext'
import { accessTokenStore } from '@/shared/api/accessTokenStore'
import { meGetMe } from '@/shared/api/generated/clients/meGetMe'
import { meGetRoles } from '@/shared/api/generated/clients/meGetRoles'
import type { AcceptInviteRequestV4 } from '@/shared/api/generated/models/AcceptInviteRequestV4'
import type { MeChangePasswordStatus200 } from '@/shared/api/generated/models/MeChangePassword'
import type { MyInternshipV4 } from '@/shared/api/generated/models/MyInternshipV4'
import type { MyRoleV4 } from '@/shared/api/generated/models/MyRoleV4'
import type { SupportSummaryV4 } from '@/shared/api/generated/models/SupportSummaryV4'

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<SessionContextValue['status']>('restoring')
  const [session, setSession] = useState<Session>()
  const [isSwitchingContext, setIsSwitchingContext] = useState(false)
  const [isTransitioningSupportSession, setIsTransitioningSupportSession] = useState(false)
  const [supportSummary, setSupportSummary] = useState<SupportSummaryV4>()
  const [isPasswordChangeRequired, setIsPasswordChangeRequired] = useState(false)
  const [termsAcceptancePayload, setTermsAcceptancePayload] = useState<unknown>()

  useEffect(
    () =>
      accessTokenStore.subscribe((token) => {
        if (token) {
          return
        }
        queryClient.clear()
        setSession(undefined)
        setSupportSummary(undefined)
        setIsPasswordChangeRequired(false)
        setTermsAcceptancePayload(undefined)
        setStatus('anonymous')
      }),
    [queryClient]
  )

  useEffect(() => {
    let active = true

    void restoreSession()
      .then((restoredSession) => {
        if (!active) {
          return
        }
        setSession(restoredSession)
        setStatus('authenticated')
      })
      .catch(() => {
        if (!active) {
          return
        }
        setSession(undefined)
        setStatus('anonymous')
      })

    return () => {
      active = false
    }
  }, [])

  const login = async (email: string, password: string) => {
    const result = await signIn(email, password)
    if (result.kind === 'code-required') {
      return result.challenge
    }
    if (result.kind === 'password-change-required') {
      setIsPasswordChangeRequired(true)
      return
    }
    if (result.kind === 'terms-acceptance-required') {
      setTermsAcceptancePayload(result.payload)
      return
    }
    queryClient.clear()
    setSession(result.session)
    setStatus('authenticated')
  }

  const verifyLoginCode = async (pendingToken: string, code: string, rememberDevice: boolean) => {
    const result = await requestLoginCodeVerification(pendingToken, code, rememberDevice)
    if (result.kind === 'password-change-required') {
      setIsPasswordChangeRequired(true)
      return
    }
    if (result.kind === 'terms-acceptance-required') {
      setTermsAcceptancePayload(result.payload)
      return
    }
    queryClient.clear()
    setSession(result.session)
    setStatus('authenticated')
  }

  const completePasswordChange = async (payload: MeChangePasswordStatus200) => {
    const result = await requestPasswordChangeCompletion(payload)
    if (result.kind === 'password-change-required') {
      return
    }
    setIsPasswordChangeRequired(false)
    if (result.kind === 'terms-acceptance-required') {
      setTermsAcceptancePayload(result.payload)
      return
    }
    queryClient.clear()
    setSession(result.session)
    setStatus('authenticated')
  }

  const completeTermsAcceptance = async () => {
    const nextSession = await requestTermsAcceptanceCompletion(termsAcceptancePayload)
    queryClient.clear()
    setTermsAcceptancePayload(undefined)
    setSession(nextSession)
    setStatus('authenticated')
  }

  const logout = async () => {
    await signOut()
    queryClient.clear()
    setSession(undefined)
    setStatus('anonymous')
  }

  const resetPassword = async (token: string, newPassword: string) => {
    const nextSession = await requestPasswordReset(token, newPassword)
    queryClient.clear()
    setSession(nextSession)
    setStatus('authenticated')
  }

  const acceptInvite = async (request: AcceptInviteRequestV4) => {
    const { roleGranted, session: nextSession } = await requestInviteAcceptance(request)
    queryClient.clear()
    setSession(nextSession)
    setStatus('authenticated')
    return roleGranted
  }

  const switchContext = async (request: () => Promise<Session>) => {
    setIsSwitchingContext(true)
    try {
      const nextSession = await request()
      queryClient.clear()
      setSession(nextSession)
    } finally {
      setIsSwitchingContext(false)
    }
  }

  const transitionSupportSession = async (
    request: () => Promise<{ session: Session; summary?: SupportSummaryV4 }>
  ) => {
    setIsTransitioningSupportSession(true)
    try {
      const { session: nextSession, summary } = await request()
      queryClient.clear()
      setSession(nextSession)
      setSupportSummary(summary)
      setStatus('authenticated')
    } finally {
      setIsTransitioningSupportSession(false)
    }
  }

  const startSupportSession = (userId: string, caseNumber: string) =>
    transitionSupportSession(() => requestSupportSessionStart(userId, caseNumber))

  const stopSupportSession = () =>
    transitionSupportSession(async () => ({ session: await requestSupportSessionStop() }))

  const switchRoleContext = (context: MyRoleV4) =>
    switchContext(() => requestRoleContextSwitch(context))

  const switchInternshipContext = (internship: MyInternshipV4) =>
    switchContext(() => requestInternshipContextSwitch(internship))

  const acceptOrgInvite = (inviteId: string) =>
    switchContext(() => requestOrgInviteAcceptance(inviteId))

  const completePayment = async (orderReference: string, paymentReference: string) => {
    const transition = requestPaymentCompletion(orderReference, paymentReference)
    await switchContext(async () => (await transition).session)
    return (await transition).orderType
  }

  const openNotificationEvent = async (eventId: Parameters<typeof requestNotificationEvent>[0]) => {
    const transition = await requestNotificationEvent(eventId)
    if (transition.session) {
      queryClient.clear()
      setSession(transition.session)
    }
    return transition.result
  }

  const refreshUser = async () => {
    const token = accessTokenStore.get()
    const { data } = await meGetMe()
    if (data.ok && token === accessTokenStore.get()) {
      setSession((current) =>
        current
          ? {
              ...current,
              user: {
                ...current.user,
                displayName: data.result.displayName,
                email: data.result.email,
                hasPortrait: data.result.hasPortrait,
              },
            }
          : current
      )
    }
  }

  const refreshContext = async () => {
    const token = accessTokenStore.get()
    const contextId = session?.activeMembership.id
    const { data } = await meGetRoles()
    if (data.ok && token === accessTokenStore.get()) {
      setSession((current) => {
        if (!current || current.activeMembership.id !== contextId) {
          return current
        }
        const updateName = (membership: Session['activeMembership']) => ({
          ...membership,
          organizationName:
            data.result.find((role) => role.orgId === membership.id)?.name ??
            membership.organizationName,
        })
        return {
          ...current,
          roleContexts: data.result,
          activeMembership: updateName(current.activeMembership),
          memberships: current.memberships.map(updateName),
        }
      })
    }
  }

  const refreshPages = async () => {
    const contextId = session?.activeMembership.id
    const token = accessTokenStore.get()
    const { data } = await meGetMe()
    if (data.ok && token === accessTokenStore.get()) {
      setSession((current) =>
        current && current.activeMembership.id === contextId
          ? { ...current, pages: data.result.pages ?? current.pages }
          : current
      )
    }
  }

  return (
    <SessionContext.Provider
      value={{
        status,
        session,
        isSwitchingContext,
        isTransitioningSupportSession,
        supportSummary,
        dismissSupportSummary: () => setSupportSummary(undefined),
        isPasswordChangeRequired,
        isTermsAcceptanceRequired: termsAcceptancePayload !== undefined,
        login,
        verifyLoginCode,
        completePasswordChange,
        completeTermsAcceptance,
        logout,
        refreshPages,
        refreshUser,
        refreshContext,
        resetPassword,
        acceptInvite,
        startSupportSession,
        stopSupportSession,
        switchRoleContext,
        switchInternshipContext,
        acceptOrgInvite,
        completePayment,
        openNotificationEvent,
      }}
    >
      {children}
    </SessionContext.Provider>
  )
}
